/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import 'react-tabs/style/react-tabs.css';
import {
  AssetType,
  CollectionStatus,
  ListingType,
  ModalName,
  PaymentGatewayType,
  TokenType,
} from '@share/enums';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { NoRecord } from 'src/styles/emotion';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { css } from '@emotion/react';
import { listing } from '@state/private';
import {
  showPercentage,
  toJSTimestamp,
  toggleBootstrapModal,
} from '@util/helper';
import { toast } from 'react-toastify';
import { useAPI, useCurrency, useHelper, useSystemConfig } from 'src/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { user } from '@state/user';
import { web3 } from '@state/web3';
import Countdown, { CountdownRenderProps } from 'react-countdown';
import LoadingContainer from '@com/utils/LoadingContainer';
import NftEventList from '@com/layouts/item-details/NftEventList';
import ShareBtn from '@com/buttons/ShareBtn';
import loadable from '@loadable/component';
import styled from '@emotion/styled';

// FIXME: lazy load modal will throw error in production build
import BidModal from '@com/modals/BidModal';
import BuyModal from '@com/modals/BuyModal';
import CancelModal from '@com/modals/CancelModal';
import CreateAuctionModal from '@com/modals/CreateAuctionModal';
import CreateSaleModal from '@com/modals/CreateSaleModal';
import IPFSBtn from '@com/buttons/IPFSBtn';
import MouseOver from '@com/utils/MouseOver';
import PaymentMethodModal from '@com/modals/PaymentMethodModal';
import SettleAuctionModal from '@com/modals/SettleAuctionModal';
import TakeHighestBidModal from '@com/modals/TakeHighestBidModal';
import TreasuryToWalletModal from '@com/modals/TreasuryToWalletModal';

const BidHistoryList = loadable(
  () => import('@com/layouts/item-details/BidHistoryList')
);
const Footer = loadable(() => import('@com/footer/Footer'));
const Header = loadable(() => import('@com/header/Header'));
const HistoryList = loadable(
  () => import('@com/layouts/item-details/HistoryList')
);
const LiveAuction = loadable(() => import('@com/layouts/home-6/LiveAuction'));
const Propertie = loadable(() => import('@com/layouts/item-details/Propertie'));
const SkeletonImage = loadable(() => import('@com/utils/SkeletonImage'));
const TopSection = loadable(() => import('@com/TopSection'));

const PropertieTab = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  grid-gap: 2em 3em;
`;

const ActionDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-right: -3rem;

  @media (min-width: 576px) {
    flex-wrap: nowrap;
  }
`;

const TitleDiv = styled.div`
  display: flex;
  align-items: center;
`;

const TabPanelStyle = {
  width: '100%',
  fontSize: '1.8em',
  minHeight: '40vh',
};

const UnorderList = styled.ul`
  display: inline-block;
  background-color: var(--primary-color7);
  border-radius: 0.5em;
  color: var(--primary-color2);
  font-size: 1.5em;
`;

const Timer = styled.div`
  font-weight: bold;
  font-size: 2em;
`;

const DescriptionBox = styled.div`
  font-size: 20px;
  line-height: 32px;
`;

const MetaInfoBlock = styled.div`
  display: grid !important;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  grid-gap: 2rem;
`;

const CustodyTips = styled.h6`
  color: var(--primary-color9);
`;

const Load3DModelBtn = styled.button`
  border-radius: 8px;
  position: absolute;
  bottom: 5%;
  right: 5%;
  background-color: var(--primary-color3);
  color: #fff;
  padding: 0.5em;
  font-size: 3em;
  opacity: 0.9;
  z-index: 1;
`;

const Background = styled.div`
  background-color: var(--primary-color);
  border-radius: 0.75rem;
  width: min(85vw, 600px) !important;
  aspect-ratio: 1;
`;

function PreviewImag({ src }: { src: string }) {
  return (
    <SkeletonImage
      alt="Item Image"
      extraStyle={css`
        width: min(85vw, 600px) !important;
        object-fit: contain !important;
      `}
      ratio={1}
      size={[256, 512, 600]}
      src={src}
    />
  );
}

function NFTDetails(): JSX.Element {
  /* Define state / hook */
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { collectionIdentifier, _tokenId, _paramsChainId } = useParams();
  const paramsChainId = Number(_paramsChainId);
  const tokenId = Number(_tokenId);

  const { getNftDetails } = useAPI();
  const buyModal = useRef<{ toggleModal: () => void }>(null);
  const bidModal = useRef<{ toggleModal: () => void }>(null);
  const { data, isSuccess, isError, refetch } = useQuery(
    [getNftDetails.name, tokenId, collectionIdentifier, paramsChainId],
    () => getNftDetails(tokenId, collectionIdentifier!, paramsChainId),
    {
      enabled:
        collectionIdentifier != null &&
        Number.isInteger(tokenId) &&
        Number.isInteger(paramsChainId),
      retry: 0,
    }
  );
  const {
    address,
    chainId,
    addressExplorerBaseURL,
    getChainName,
    isValidRequest,
  } = web3.useContainer();
  const { t, ready } = useTranslation();
  const { config } = useSystemConfig();
  const { userData, isLogin, isNoMetamask } = user.useContainer();
  const {
    displayName,
    getUserIdentifier,
    showDescription,
    displayCurrency,
    showAddressLink,
  } = useHelper();
  // FIXME: cannot use listing hook here, so create new state for passing to payment modal
  const [listingType, setListingType] = useState(ListingType.BUY);
  const [isModelViewerLoaded, setIsModelViewerLoaded] = useState(false);
  const { isFiatCurrency } = useCurrency();

  /* Custom Data Object for render or other usages */
  const creator = data?.creator;
  const owner = data?.owner?.[0];
  const order = data?.orders?.[0];
  const orderInfo = order?.order_info;
  const collection = data?.collection;
  const userUUID = userData?.user?.uuid;
  const latestBidPrice =
    orderInfo?.current_price === String(0)
      ? orderInfo?.minimum_bid
      : orderInfo?.current_price;
  const bidBasePrice =
    (Number(latestBidPrice) *
      (100 + Number(orderInfo?.bid_increase_percentage))) /
    100;

  const isOwned = userUUID === owner?.uuid;
  const isBidOrder = orderInfo?.bid_increase_percentage != null;
  const isBidEnded = Date.now() > toJSTimestamp(orderInfo?.end_at_timestamp);
  const isLatestBidder =
    isLogin && // Prevent null === null when not login
    userData?.user?.wallet?.[0]?.wallet_address === orderInfo?.bidder_address;

  const tabIndexMap: Record<string, number> = {
    event: 0,
    propertie: 1,
    history: 2,
    bid: 3,
  };

  const tokenTypeMap = {
    [TokenType.ERC721]: 'ERC-721',
    [TokenType.ERC1155]: 'ERC-1155',
    [TokenType.SOULBOUND]: t('itemDetails.soulbound'),
  };

  /* Render control function / logic */
  const toggleModalWithDebounce = useDebouncedCallback(
    (name: ModalName) => {
      toggleBootstrapModal(name);
    },
    1000,
    { leading: true, trailing: false }
  );

  const togglePaymentMethodModal = async (type: ListingType) => {
    const modal =
      type === ListingType.BID ? ModalName.CREATE_BID : ModalName.CREATE_BUY;

    // If it is wallet account, currently only metamask supported
    if (!isNoMetamask) {
      toggleBootstrapModal(modal);
      return;
    }

    /**
     * If only 1 fiat payment gateway supported for the platform, skip selecting
     * payment gateway
     */
    const gateways = config?.payment_gateways?.filter(
      (gateway) =>
        gateway.payment_gateway_type !== PaymentGatewayType.CRYPTOCURRENCY
    );
    if (gateways?.length === 1) {
      toggleBootstrapModal(modal);
      return;
    }

    setListingType(type);
    toggleModalWithDebounce(ModalName.SELECT_PAYMENT);
  };

  const toggleBidBuyModal = useDebouncedCallback(
    async (type: ListingType) => {
      if (!(await isValidRequest(paramsChainId))) return;

      // If crypto
      if (!isFiatCurrency(order?.currency?.id)) {
        if (!address) {
          toast.error(t('toastMessage.connectWalletRequired'));
          return; // Always return since after login here chainId update will have delay, then always show below error msg
        }
        if (chainId !== paramsChainId) {
          toast.error(t('toastMessage.supportedChainRequired'));
          return;
        }
      }
      (type === ListingType.BUY ? buyModal : bidModal).current?.toggleModal();
    },
    1000,
    { leading: true, trailing: false }
  );

  const loadModelViewer = async () => {
    const scriptSrc =
      'https://cdn.jsdelivr.net/npm/@google/model-viewer@3.1.1/dist/model-viewer.min.js';
    const existingScriptTag = document.querySelector(
      `script[src='${scriptSrc}']`
    );
    const scriptTag = document.createElement('script');
    scriptTag.src = scriptSrc;
    scriptTag.type = 'module';
    scriptTag.addEventListener('load', () => setIsModelViewerLoaded(true));
    document.head.appendChild(scriptTag);
    if (existingScriptTag) document.head.removeChild(existingScriptTag);
  };

  /* Data Change Listeners - useEffect/useMemo/useCallback */
  useEffect(() => {
    // Prevent null also showing the toast
    if (
      collection?.status &&
      collection.status !== CollectionStatus.LISTING &&
      ready
    ) {
      toast.error(t('toastMessage.collectionNotAvailable'), {
        closeOnClick: false,
        closeButton: false,
        autoClose: false,
        hideProgressBar: true,
        position: 'bottom-center',
        draggable: false,
        toastId: 'collection-not-available',
      });
    }

    return () => {
      if (collection?.status !== CollectionStatus.LISTING) {
        toast.dismiss('collection-not-available');
      }
    };
  }, [collection?.status, ready]);

  useEffect(() => {
    if (data?.asset_type === AssetType.MODEL) {
      // Make sure webgl enable
      const webgl = document.createElement('canvas').getContext('webgl');
      if (webgl) loadModelViewer();
    }
  }, [data?.asset_type]);

  const PARAM_NAME = 'type';
  const selectedIndex = useMemo(() => {
    return tabIndexMap[String(searchParams.get(PARAM_NAME))] || 0;
  }, [location.search]);

  /* Styling */
  const Href = useMemo(
    () => styled.a`
      pointer-events: ${data?.asset ? 'unset' : 'none'};
    `,
    [data?.asset]
  );

  /* Render */
  return (
    <listing.Provider>
      <Header />
      <TopSection />
      <div className="content-container tf-section tf-item-details style-2">
        <LoadingContainer
          iconClassName="w-100 d-flex justify-content-center align-items-center"
          isError={isError}
          isSuccess={isSuccess}
        >
          <div className="row">
            <div className="col-xl-6 col-md-12">
              <div className="content-left d-flex justify-content-center mb-5">
                {data?.asset_type === AssetType.IMAGE && (
                  <Href href={data?.asset} rel="noreferrer" target="_blank">
                    <PreviewImag src={data?.asset} />
                  </Href>
                )}
                {data?.asset_type === AssetType.MODEL && (
                  <Background>
                    {isModelViewerLoaded && data?.asset ? (
                      <model-viewer
                        autoplay
                        camera-controls
                        ar-modes="webxr scene-viewer quick-look"
                        id="model-viewer-3d"
                        reveal="manual"
                        src={data?.asset}
                      >
                        <div slot="poster">
                          <PreviewImag src={data?.preview} />
                          <Load3DModelBtn
                            className="sc-button"
                            onClick={() => {
                              document
                                .querySelector<any>('#model-viewer-3d')
                                ?.dismissPoster();
                            }}
                          >
                            <i aria-hidden className="fa fa-cube" />
                          </Load3DModelBtn>
                        </div>
                      </model-viewer>
                    ) : (
                      <PreviewImag src={data?.preview} />
                    )}
                  </Background>
                )}
              </div>
            </div>
            <div className="col-xl-6 col-md-12">
              <div className="content-right">
                <div className="sc-item-details">
                  <TitleDiv className="meta-item">
                    <h2>{data?.name}</h2>
                    {/* <div className="right">
                        <span className="viewed eye mg-r-8">12</span>
                        <span className="liked heart wishlist-button mg-r-8">
                          <span className="number-like">23</span>
                        </span>
                      </div> */}
                    <MouseOver
                      className="d-flex mr-2"
                      text={t('itemDetails.ipfsDes')}
                    >
                      <IPFSBtn src={data?.token_uri} />
                    </MouseOver>
                    <MouseOver text={t('itemDetails.shareDes')}>
                      <ShareBtn />
                    </MouseOver>
                  </TitleDiv>
                  <MetaInfoBlock className="client-infor sc-card-product">
                    <div className="meta-info">
                      <div className="author w-100">
                        <div className="avatar">
                          <SkeletonImage
                            alt="Owner Avatar"
                            ratio={1}
                            size={[64, 128]}
                            src={owner?.avatar}
                          />
                        </div>
                        <div className="info flex-grow-1 w-75">
                          <span>{t('itemDetails.ownedBy')}</span>
                          {owner?.uuid ? (
                            <Link
                              className="d-flex"
                              to={`/profile/${getUserIdentifier(owner)}`}
                            >
                              <h6 className="text-truncate">
                                {displayName(owner?.name)}
                              </h6>
                              {owner?.wallet?.is_treasury && (
                                <CustodyTips className="ml-2">
                                  {`(${t('itemDetails.custody')})`}
                                </CustodyTips>
                              )}
                            </Link>
                          ) : (
                            <a
                              href={`${addressExplorerBaseURL(paramsChainId)}/${
                                owner?.wallet?.wallet_address
                              }`}
                              rel="noreferrer"
                              target="_blank"
                            >
                              <h6>
                                {displayName(owner?.wallet?.wallet_address)}
                              </h6>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="meta-info">
                      <div className="author w-100">
                        <div className="avatar">
                          <SkeletonImage
                            alt="Creator Avatar"
                            ratio={1}
                            size={[64, 128]}
                            src={creator?.avatar}
                          />
                        </div>
                        <div className="info flex-grow-1 w-75">
                          <span>{t('itemDetails.createdBy')} </span>
                          {creator?.uuid ? (
                            <h6 className="w-100 text-truncate">
                              <Link
                                to={`/profile/${getUserIdentifier(creator)}`}
                              >
                                {displayName(creator?.name || creator?.address)}
                              </Link>
                            </h6>
                          ) : (
                            <a
                              href={`${addressExplorerBaseURL(paramsChainId)}/${
                                creator?.address
                              }`}
                              rel="noreferrer"
                              target="_blank"
                            >
                              <h6>{displayName(creator?.address)}</h6>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </MetaInfoBlock>
                  <DescriptionBox>
                    {showDescription(data?.description) ||
                      t('general.noDescription')}
                  </DescriptionBox>
                  <div className="meta-item-details">
                    <div className="item-style-2 item-details">
                      {order && (
                        <div>
                          {isBidOrder ? (
                            <ul className="list-details mb-4">
                              <li>
                                <span>{t('itemDetails.currentBid')}</span>
                                <h6>
                                  {displayCurrency(
                                    latestBidPrice,
                                    order?.currency?.name
                                  )}
                                </h6>
                              </li>
                              <li>
                                <span>{t('itemDetails.bidIncrease')}</span>
                                <h6>
                                  {showPercentage(
                                    orderInfo?.bid_increase_percentage
                                  )}
                                </h6>
                              </li>
                            </ul>
                          ) : (
                            <ul className="list-details mb-4">
                              <li>
                                <span>{t('itemDetails.currentPrice')}</span>
                                <h4>
                                  {displayCurrency(
                                    orderInfo?.selling_price,
                                    order?.currency.name
                                  )}
                                </h4>
                              </li>
                            </ul>
                          )}
                        </div>
                      )}
                      <ul className="list-details">
                        <li>
                          <span>{t('itemDetails.tokenId')}</span>
                          <h6>{data?.token_id || 0}</h6>
                        </li>
                        {getChainName(paramsChainId) && (
                          <li>
                            <span>{t('itemDetails.network')}</span>
                            <h6>{getChainName(paramsChainId)}</h6>
                          </li>
                        )}
                        {data?.token_type && (
                          <li>
                            <span>{t('itemDetails.tokenType')}</span>
                            <h6>{tokenTypeMap[data.token_type]}</h6>
                          </li>
                        )}
                        <li>
                          <span>{t('itemDetails.totalQuantity')}</span>
                          <h6>{data?.initial_quantity || 0}</h6>
                        </li>
                      </ul>
                    </div>

                    <div className="item-style-2 item-details d-flex flex-column justify-content-between">
                      <ul className="list-details flex-grow-1 mb-4">
                        {collection?.status === CollectionStatus.LISTING && (
                          <li>
                            <span>{t('itemDetails.collection')}</span>
                            {collection?.name && (
                              <Link
                                className="text-truncate"
                                to={`/collection/${paramsChainId}/${collection?.address}`}
                              >
                                <h6 className="text-truncate">
                                  {collection.name}
                                </h6>
                              </Link>
                            )}
                          </li>
                        )}
                        {collection?.address && (
                          <li>
                            <span>{t('itemDetails.contractAddress')}</span>
                            <h6>
                              {showAddressLink(
                                collection.address,
                                paramsChainId
                              )}
                            </h6>
                          </li>
                        )}
                      </ul>

                      <ul className="list-details flex-grow-1">
                        <li>
                          <span>{t('itemDetails.artistRoyalties')}</span>
                          <h6>{showPercentage(Number(collection?.royalty))}</h6>
                        </li>
                      </ul>

                      {isBidOrder && (
                        <UnorderList className="list-details flex-grow-1 mt-4">
                          <div className="featured-countdown d-flex justify-content-center align-items-center h-100">
                            <Countdown
                              date={toJSTimestamp(orderInfo?.end_at_timestamp)}
                              renderer={(props: CountdownRenderProps) =>
                                props.completed ? (
                                  <div className="font-weight-bold fs-30 center mt-3">
                                    {t('live.bidEnded')}
                                  </div>
                                ) : (
                                  <div className="center">
                                    <div className="mb-3">
                                      {`${t(
                                        'itemDetails.saleEndAt'
                                      )} ${new Date(
                                        toJSTimestamp(
                                          orderInfo?.end_at_timestamp
                                        )
                                      ).toLocaleString()}`}
                                    </div>
                                    <Timer className="d-flex flex-wrap justify-content-around">
                                      {props.days > 0 && (
                                        <div>
                                          <div>{props.days}</div>
                                          <p>{t('itemDetails.days')}</p>
                                        </div>
                                      )}
                                      <div>
                                        <div>{props.hours}</div>
                                        <p>{t('itemDetails.hours')}</p>
                                      </div>
                                      <div>
                                        <div>{props.minutes}</div>
                                        <p>{t('itemDetails.minutes')}</p>
                                      </div>
                                      <div>
                                        <div>{props.seconds}</div>
                                        <p>{t('itemDetails.seconds')}</p>
                                      </div>
                                    </Timer>
                                  </div>
                                )
                              }
                            />
                          </div>
                        </UnorderList>
                      )}
                    </div>
                  </div>
                  {data?.token_type !== TokenType.SOULBOUND && (
                    <>
                      {collection?.status === CollectionStatus.LISTING && (
                        <>
                          {/* Sell */}
                          <div>
                            {!order && isOwned && (
                              <ActionDiv>
                                <button
                                  aria-label={t('itemDetails.sellNow')}
                                  className="sc-button loadmore style note fl-button pri-3 mr-5 w-100"
                                  data-test="open-sale-modal-btn"
                                  type="button"
                                  onClick={() => {
                                    togglePaymentMethodModal(ListingType.BUY);
                                  }}
                                >
                                  <span>{t('itemDetails.sellNow')}</span>
                                </button>
                                <button
                                  className="sc-button loadmore style note fl-button pri-3 mr-5 w-100"
                                  data-test="open-auction-modal-btn"
                                  type="button"
                                  onClick={() => {
                                    togglePaymentMethodModal(ListingType.BID);
                                  }}
                                >
                                  <span>{t('itemDetails.placeAuction')}</span>
                                </button>
                              </ActionDiv>
                            )}
                          </div>
                          {/* Buy */}
                          <div>
                            {order && !isBidOrder && !isOwned && (
                              <ActionDiv>
                                <button
                                  className="sc-button loadmore style bag fl-button pri-3 mr-5 w-100"
                                  type="button"
                                  onClick={() =>
                                    toggleBidBuyModal(ListingType.BUY)
                                  }
                                >
                                  <span>{t('itemDetails.buyNow')}</span>
                                </button>
                              </ActionDiv>
                            )}
                          </div>
                          {/* Bid */}
                          <div>
                            {isBidOrder && !isOwned && !isBidEnded && (
                              <ActionDiv>
                                <button
                                  className="sc-button loadmore style bag fl-button pri-3 mr-5 w-100"
                                  type="button"
                                  onClick={() =>
                                    toggleBidBuyModal(ListingType.BID)
                                  }
                                >
                                  <span>{t('itemDetails.placeBid')}</span>
                                </button>
                              </ActionDiv>
                            )}
                          </div>
                        </>
                      )}
                      {/* Take Highest Bid  */}
                      <div>
                        {isBidOrder && isOwned && (
                          <ActionDiv>
                            {orderInfo?.current_price !== String(0) && (
                              <button
                                className="sc-button loadmore style rocket fl-button pri-3 mr-5 w-100"
                                type="button"
                                onClick={() => {
                                  toggleModalWithDebounce(
                                    ModalName.TAKE_HIGHEST
                                  );
                                }}
                              >
                                <span>{t('itemDetails.takeHighestBid')}</span>
                              </button>
                            )}
                          </ActionDiv>
                        )}
                      </div>
                      {/* Cancel sell / auction */}
                      <div>
                        {order && isOwned && (
                          <ActionDiv>
                            <button
                              aria-label={t('itemDetails.cancelSell')}
                              className="sc-button loadmore style fl-button pri-3 mr-5 w-100"
                              data-test="open-cancel-modal-btn"
                              type="button"
                              onClick={() => {
                                toggleModalWithDebounce(ModalName.CANCEL);
                              }}
                            >
                              <span className="p-0">
                                {t('itemDetails.cancelSell')}
                              </span>
                            </button>
                          </ActionDiv>
                        )}
                      </div>
                      {/* + Withdraw  */}
                      {!order && isOwned && owner?.wallet?.is_treasury && (
                        <button
                          className="sc-button loadmore style fl-button pri-3 w-100"
                          type="button"
                          onClick={() => {
                            toggleModalWithDebounce(ModalName.WITHDRAW);
                          }}
                        >
                          {t('itemDetails.toWeb3Wallet')}
                        </button>
                      )}
                      {/* Settle */}
                      {order && isBidEnded && isLatestBidder && (
                        <button
                          className="sc-button loadmore style fl-button pri-3 w-100"
                          type="button"
                          onClick={() => {
                            toggleModalWithDebounce(ModalName.SETTLE);
                          }}
                        >
                          {t('itemDetails.settleAuction')}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="row mt-5">
            <div className="flat-tabs themesflat-tabs w-100 p-4">
              <Tabs
                selectedIndex={selectedIndex}
                onSelect={(index) => {
                  navigate(
                    `${location.pathname}?${PARAM_NAME}=${
                      Object.keys(tabIndexMap)[index]
                    }`
                  );
                }}
              >
                <TabList className="mb-5 px-4">
                  {Number(data?.event_count) > 0 && (
                    <Tab>{t('itemDetails.events')}</Tab>
                  )}
                  <Tab>{t('itemDetails.properties')}</Tab>
                  <Tab>{t('itemDetails.history')}</Tab>
                  {isBidOrder && <Tab>{t('itemDetails.bidHistory')}</Tab>}
                </TabList>

                {Number(data?.event_count) > 0 && (
                  <TabPanel style={TabPanelStyle}>
                    <NftEventList
                      chainId={paramsChainId}
                      collectionAddress={collection?.address!}
                      isOwned={isOwned}
                      tokenId={tokenId}
                    />
                  </TabPanel>
                )}

                <TabPanel style={TabPanelStyle}>
                  {Number(data?.attributes?.length) > 0 ? (
                    <PropertieTab>
                      {data?.attributes.map((item, index) => (
                        <Propertie key={index} {...item} />
                      ))}
                    </PropertieTab>
                  ) : (
                    <NoRecord>
                      {t('itemDetails.collectionNotSupportProperties')}
                    </NoRecord>
                  )}
                </TabPanel>

                <TabPanel style={TabPanelStyle}>
                  <HistoryList
                    chainId={paramsChainId}
                    collectionAddress={collection?.address!}
                    tokenId={tokenId}
                  />
                </TabPanel>

                {isBidOrder && (
                  <TabPanel style={TabPanelStyle}>
                    <BidHistoryList orderId={order?.id!} />
                  </TabPanel>
                )}
              </Tabs>
            </div>
          </div>
        </LoadingContainer>
        {isSuccess && <LiveAuction />}
      </div>
      <Footer />
      {isLogin && data?.token_type !== TokenType.SOULBOUND && (
        <>
          {/* Don't remove this checking cause it will init two checkout script */}
          {isBidOrder ? (
            <BidModal
              ref={bidModal}
              basePrice={bidBasePrice}
              collectionAddress={collection?.address!}
              contractChainId={paramsChainId}
              currencyId={order?.currency?.id!}
              loadLaunchpadData={refetch}
              orderId={order?.id!}
              paymentGateway={order?.payment_method?.payment_gateway?.name!}
              tokenId={tokenId}
            />
          ) : (
            <BuyModal
              ref={buyModal}
              collectionAddress={collection?.address!}
              contractChainId={paramsChainId}
              currencyId={order?.currency?.id!}
              loadLaunchpadData={refetch}
              orderId={order?.id!}
              paymentGateway={order?.payment_method?.payment_gateway?.name!}
              price={orderInfo?.selling_price!}
              tokenId={tokenId}
            />
          )}
          <CreateSaleModal
            collectionAddress={collection?.address!}
            loadLaunchpadData={refetch}
            royalty={collection?.royalty!}
            tokenId={tokenId}
          />
          <CreateAuctionModal
            collectionAddress={collection?.address!}
            loadLaunchpadData={refetch}
            royalty={collection?.royalty!}
            tokenId={tokenId}
          />
          <TakeHighestBidModal
            collectionAddress={collection?.address!}
            currencyId={order?.currency?.id!}
            loadLaunchpadData={refetch}
            orderId={order?.id!}
            paymentMethod={order?.payment_method!}
            price={Number(orderInfo?.current_price!)}
            royalty={collection?.royalty!}
            tokenId={tokenId}
          />
          <SettleAuctionModal
            collectionAddress={collection?.address!}
            currencyId={order?.currency.id}
            price={Number(orderInfo?.current_price)}
            tokenId={tokenId}
          />
          <CancelModal
            collectionAddress={collection?.address!}
            contractChainId={paramsChainId}
            currencyId={order?.currency.id}
            loadLaunchpadData={refetch}
            orderId={order?.id!}
            price={Number(
              isBidOrder ? latestBidPrice : orderInfo?.selling_price
            )}
            tokenId={tokenId}
          />
          <TreasuryToWalletModal
            collectionAddress={collection?.address!}
            contractChainId={paramsChainId}
            tokenId={tokenId}
          />
          <PaymentMethodModal listingType={listingType} />
        </>
      )}
    </listing.Provider>
  );
}

export default NFTDetails;
